/**
 * ValueBox — solid (or outlined) coloured shape containing a big number
 * and an optional small label.
 *
 * Replaces three private implementations:
 *   - IhsTopScorers `.heroGoalsBox` (rect, lg, solid, label INSIDE the box)
 *   - IhsTopScorers `.rowGoalsBox` (rect, sm, solid, value only)
 *   - IhsShots `CircleStat` (circle, md, outline OR solid, label OUTSIDE below)
 *
 * Variants:
 *   shape:       'rect' | 'circle'
 *   size:        'sm' (32×32) | 'md' (36×36) | 'lg' (56×56)
 *   variant:     'solid' (background = color, text = white)
 *                'outline' (white fill, coloured border, dark text)
 *   labelPlace:  'inside' — label sits inside the coloured box, smaller and inverse
 *                'below'  — label renders under the box in muted grey
 *                          (default when `label` is set)
 */

import type { CSSProperties, ReactElement, ReactNode } from 'react';
import { z } from 'zod';
import type { PrimitiveMeta } from './_meta.js';
import styles from './ValueBox.module.css';
import { inkOn } from '../../lib/inkOn.js';

/**
 * `value` is `ReactNode` (consumers may pass a custom span) but the wire
 * shape captures the LLM-portable values: number or string. Variant axes
 * (shape × size × variant × labelPlace) are exposed so the planner knows
 * the full configuration grid.
 */
const dataShape = z.object({
  value: z.union([z.string(), z.number()]),
  label: z.string().optional(),
  /** Colour used as fill (solid) or border (outline). */
  color: z.string(),
  shape: z.enum(['rect', 'circle']).optional(),
  size: z.enum(['sm', 'md', 'lg']).optional(),
  variant: z.enum(['solid', 'outline']).optional(),
  /** 'inside' — label inside the coloured box. 'below' — under it. */
  labelPlace: z.enum(['inside', 'below']).optional(),
});

export const META: PrimitiveMeta = {
  name: 'value-box',
  description: 'Coloured rect/circle holding a big value + optional label.',
  composable: true,
  dataShape,
  densities: ['compact', 'standard'],
  breakpoints: { mobile: true, wide: true },
  animation: 'none',
};

interface ValueBoxProps {
  value: ReactNode;
  /** Optional tiny label. When absent, no label cell renders. */
  label?: string;
  /** Colour used as the fill (solid) or border (outline). */
  color: string;
  shape?: 'rect' | 'circle';
  size?: 'sm' | 'md' | 'lg';
  variant?: 'solid' | 'outline';
  /** Where the label sits relative to the box. Defaults to 'below'. */
  labelPlace?: 'inside' | 'below';
}

function sizeClass(size: NonNullable<ValueBoxProps['size']>): string {
  if (size === 'sm') return styles.sm ?? '';
  if (size === 'lg') return styles.lg ?? '';
  return styles.md ?? '';
}

function shapeClass(shape: NonNullable<ValueBoxProps['shape']>): string {
  return shape === 'circle' ? (styles.circle ?? '') : (styles.rect ?? '');
}

export function ValueBox({
  value,
  label,
  color,
  shape = 'rect',
  size = 'md',
  variant = 'solid',
  labelPlace = 'below',
}: ValueBoxProps): ReactElement {
  const inlineStyle: CSSProperties =
    variant === 'solid'
      ? {
          background: color,
          // Ink by the fill's luminance — `--text-inverse` is the ink on the
          // ACCENT, not "white" (navy on navy on Bet247, 2026-09-08).
          color: inkOn(color),
          border: `1px solid ${color}`,
        }
      : {
          background: 'var(--bg-surface, #FFFFFF)',
          color: 'var(--text-primary, #1A1A1A)',
          border: `2px solid ${color}`,
        };

  const boxClass = `${styles.box} ${shapeClass(shape)} ${sizeClass(size)}`;

  // Inside-label variant: a single coloured box with value AND label
  // stacked vertically, both inverse-coloured (used by IhsTopScorers
  // hero rows).
  if (label !== undefined && labelPlace === 'inside') {
    return (
      <span className={`${boxClass} ${styles.stacked}`} style={inlineStyle}>
        <span className={styles.valueInside}>{value}</span>
        <span className={styles.labelInside}>{label}</span>
      </span>
    );
  }

  // Below-label variant: box with label rendered underneath in grey
  // (used by IhsShots CircleStat).
  if (label !== undefined) {
    return (
      <div className={styles.wrap}>
        <span className={boxClass} style={inlineStyle}>
          {value}
        </span>
        <span className={styles.labelBelow}>{label}</span>
      </div>
    );
  }

  // Label-less variant — used by `rowGoalsBox` in IhsTopScorers list rows.
  return (
    <span className={boxClass} style={inlineStyle}>
      {value}
    </span>
  );
}

export default ValueBox;
